import { useCallback, useEffect, useRef, useState } from 'react';
import { usePreviewLock } from '@/shared/hooks/usePreviewLock';

export const SAVE_FAILED_MESSAGE =
    'Your latest changes could not be saved. Check your connection and try again.';

/**
 * Saves a form's progress to the server and tracks whether what is on screen
 * has been saved.
 *
 * `payload` is what a save sends; `persist(payload)` sends it and resolves
 * once the server has it. Saves run one at a time in the order asked, and
 * each sends the payload as it stands when its turn comes, so a slow save
 * can't land after a newer one and the first save can't create a record
 * twice.
 *
 * Nothing counts as unsaved until markClean() records a baseline (call it
 * once the form has been filled from the server or seeded), so filling a
 * form is not mistaken for an edit. When the component unmounts with
 * unsaved changes and `canSave` true, one last save is sent: leaving by the
 * browser's Back button can't be intercepted, so this is what keeps it.
 *
 * Returns { save, markClean, hasUnsavedChanges, saveState }. save({ force })
 * sends even when nothing changed (to create the record the first time) and
 * rejects when the save fails. save({ payload }) sends a different shape of
 * the same form state, such as the stricter one a submit sends; it does not
 * mark the form clean, since the server then holds less than the form does
 * (the caller marks it clean once the submit succeeds). saveState is { status: 'idle' | 'saving' |
 * 'saved' | 'error' | 'preview', savedAt?, message? }.
 *
 * While a role preview is active nothing is sent: save() resolves without
 * calling `persist`, nothing counts as unsaved (so leaving never prompts),
 * and saveState.status is 'preview'. save() resolves { skipped: 'preview' }
 * so a caller never reports the skipped save as saved.
 */
export default function useSavedProgress({ payload, persist, canSave }) {
    const json = JSON.stringify(payload);
    const [savedJson, setSavedJson] = useState(null);
    const [saveState, setSaveState] = useState({ status: 'idle' });
    const latest = useRef({});
    const previewing = usePreviewLock();
    latest.current = { payload, json, persist, canSave, previewing };
    // Read by queued saves, which run between renders
    const savedJsonRef = useRef(null);
    const chainRef = useRef(Promise.resolve());

    const markClean = useCallback(() => {
        savedJsonRef.current = latest.current.json;
        setSavedJson(latest.current.json);
    }, []);

    const save = useCallback(({ force = false, payload: override } = {}) => {
        const run = async () => {
            const {
                payload: body,
                json: sent,
                persist: send,
                previewing: skip,
            } = latest.current;
            if (skip) return { skipped: 'preview' };
            if (!force && sent === savedJsonRef.current) return;
            setSaveState({ status: 'saving' });
            try {
                await send(override ?? body);
            } catch (err) {
                setSaveState({
                    status: 'error',
                    message:
                        err?.response?.data?.message || SAVE_FAILED_MESSAGE,
                });
                throw err;
            }
            // A narrower override (the submit payload drops unnamed rows)
            // is not what the form holds, so it must not become the clean
            // baseline: a refused submit would then read as saved while the
            // server lacks those rows. A successful submit marks the form
            // clean itself.
            if (override === undefined) {
                savedJsonRef.current = sent;
                setSavedJson(sent);
            }
            setSaveState({ status: 'saved', savedAt: new Date() });
        };
        const next = chainRef.current.catch(() => {}).then(run);
        chainRef.current = next;
        return next;
    }, []);

    useEffect(
        () => () => {
            const {
                canSave: ok,
                json: current,
                previewing: skip,
            } = latest.current;
            if (
                ok &&
                !skip &&
                savedJsonRef.current !== null &&
                current !== savedJsonRef.current
            ) {
                save().catch(() => {});
            }
        },
        [save]
    );

    return {
        save,
        markClean,
        hasUnsavedChanges:
            !previewing && savedJson !== null && json !== savedJson,
        saveState: previewing ? { status: 'preview' } : saveState,
    };
}
